/**
 * Body-copy typography for public, SEO-facing prose.
 *
 * Lives in a plain module, NOT in `markdown-view.tsx`, because that file is
 * `'use client'`. A Server Component importing a value from a client module gets
 * a client reference, so the class arrives `undefined` and the element renders
 * completely unstyled. That is the same failure that shipped the free-tool CTAs
 * without their gradient and the FAQ arrays without their markup.
 *
 * Built entirely from `[&_x]:` descendant variants, so it styles any JSX subtree,
 * not only rendered markdown. Worth knowing: `@tailwindcss/typography` is NOT
 * installed here, so `prose` / `prose-invert` generate no CSS whatsoever. This is
 * the replacement, and it is what gives a hand-written `<ul>` its bullets back
 * under Tailwind's preflight, which strips list-style and padding.
 */
export const ARTICLE_PROSE =
  'text-[rgb(var(--foreground-secondary))] leading-relaxed ' +
  '[&_a]:text-[rgb(var(--primary))] [&_a]:underline [&_a]:underline-offset-4 [&_a]:break-words ' +
  '[&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-[rgb(var(--foreground))] ' +
  '[&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-[rgb(var(--foreground))] ' +
  '[&_h4]:mt-6 [&_h4]:mb-2 [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-[rgb(var(--foreground))] ' +
  '[&_p]:my-4 [&_p]:text-lg ' +
  '[&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-2 [&_li]:text-lg ' +
  '[&_strong]:font-semibold [&_strong]:text-[rgb(var(--foreground))] ' +
  '[&_em]:italic ' +
  '[&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:border-[rgb(var(--primary))] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[rgb(var(--foreground-secondary))] ' +
  // GFM tables — the whole point of routing pSEO bodies through Streamdown.
  '[&_table]:my-8 [&_table]:w-full [&_table]:max-w-full [&_table]:border-collapse [&_table]:text-base ' +
  '[&_thead]:border-b-2 [&_thead]:border-[rgb(var(--surface-border))] ' +
  '[&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:font-semibold [&_th]:text-[rgb(var(--foreground))] [&_th]:whitespace-nowrap ' +
  '[&_td]:border-b [&_td]:border-[rgb(var(--surface-border))] [&_td]:px-4 [&_td]:py-3 [&_td]:align-top ' +
  // Inline imagery embedded in the body.
  '[&_img]:my-8 [&_img]:mx-auto [&_img]:rounded-xl [&_img]:border [&_img]:border-[rgb(var(--surface-border))] ' +
  '[&_hr]:my-10 [&_hr]:border-[rgb(var(--surface-border))] ' +
  '[&_code]:rounded [&_code]:bg-[rgb(var(--surface))] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-sm ' +
  '[&_pre]:my-6 [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:border [&_pre]:border-[rgb(var(--surface-border))] [&_pre]:bg-[rgb(var(--surface))] [&_pre]:p-4';
